import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { postData, fetchData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const ReceiptCreate = ({ match }) => {
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [invoicedeatils, setInvoicedeatils] = useState([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState("1");

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        BillDate: '',
        BillNo: 0,
        CustomerId: '',
        PaymentType: '',
        Amount: 0,
        Remarks: '',
        createdBy: userId,
        isSubmit: false,
    });

    // Handle customer selection
    const handleCustomerSelect = (selectedOption) => {
        setSelectedCustomer(selectedOption);
        setFormData((prevData) => ({
            ...prevData,
            CustomerId: selectedOption ? selectedOption.value : '',
            CreatedBy: userId,
        }));

        if (selectedOption) {
            fetchData('ReceiptApi', `/PartyReceiptBill/${selectedOption.value}/${companyId}`, token)
                .then((response) => {
                    if (response.responseCode === '1111') {
                        const updatedInvoicedetailsSorted = response.response_data;
                        const totalSaleAmount = updatedInvoicedetailsSorted.reduce((total, invdetail) => {
                            return total + invdetail.SaleAmount;
                        }, 0);

                        setFormData((prevData) => ({
                            ...prevData,
                            TotalBalAmt: totalSaleAmount,
                        }));
                        setInvoicedeatils(updatedInvoicedetailsSorted);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching invoice details:', error);
                });
        }
    };

    useEffect(() => {
        fetchCustomerData();
    }, []);

    const fetchCustomerData = () => {
        fetchData('ReceiptApi', `/Party/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Name,
                    value: customer.ID,
                }));
                setCustomerOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching customer data:', error);
            });
    };

    const submitData = async (e) => {
        setIsLoading(true);

        if (!formData.CustomerId || formData.CustomerId === 0) {
            setErrorMessage('Customer is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.Amount || formData.Amount === 0) {
            setErrorMessage('Amount must be Greater than 0!');
            setIsLoading(false);
            return;
        }

        if (formData.TotalBalAmt != 0) {
            if (!invoicedeatils || invoicedeatils.length === 0) {
                setErrorMessage('Invoice Details are required!');
                setIsLoading(false);
                return;
            }
        }

        const updatedFormData = {
            ...formData,
            CreatedBy: userId,
        };

        const createData = {
            PaymentAndReceipt: updatedFormData,
            PaymentAndReceiptRentCollection: invoicedeatils,
        };

        try {
            await postData('ReceiptApi', createData, token);
            setSuccessMessage('Receipt Created successfully!');
            setErrorMessage('');
            history.push('/ReceiptIndex');
            setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
        } catch (error) {
            console.error('Receipt Create error:', error);
            setErrorMessage('Failed to create receipt. Please try again.');
            setIsLoading(false);
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name === 'Amount') {
            let newAmount = parseFloat(value);
            if (isNaN(newAmount)) {
                newAmount = 0;
            }

            if (formData.TotalBalAmt != 0) {
                const restrictedAmount = Math.min(newAmount, formData.TotalBalAmt);
                setFormData((prevData) => ({
                    ...prevData,
                    [name]: type === 'checkbox' ? checked : restrictedAmount,
                }));

                let remainingBalance = restrictedAmount;
                const updatedInvoicedeatils = invoicedeatils.map((invdetail) => {
                    const amountForThisRow = Math.min(invdetail.SaleAmount, remainingBalance);
                    remainingBalance -= amountForThisRow;
                    const updatedAmount = Math.min(amountForThisRow, invdetail.SaleAmount);
                    const balanceForThisRow = Math.max(invdetail.SaleAmount - updatedAmount, 0);

                    return {
                        ...invdetail,
                        Amount: parseFloat(updatedAmount),
                        BalanceAmount: parseFloat(balanceForThisRow),
                    };
                });
                setInvoicedeatils(updatedInvoicedeatils);
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    [name]: type === 'checkbox' ? checked : newAmount,
                }));
            }
        } else if (name === 'PaymentType') {
            setSelectedPaymentType(value);
            setFormData((prevData) => ({
                ...prevData,
                PaymentType: value,
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        setFormData((prevData) => ({
            ...prevData,
            BillDate: formattedDate,
            PaymentType: 1,
        }));
    }, []);

    const handleCalculation = (e, index, inputType) => {
        let name, value;
        if (typeof e === 'object') {
            name = e.target.name;
            value = e.target.value;
        } else {
            name = inputType;
            value = e;
        }

        const updatedInvoicedeatils = invoicedeatils.map((invdetail, idx) => {
            if (idx === index) {
                const updatedAmount = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
                const saleAmount = parseFloat(invdetail.SaleAmount);

                if (parseFloat(updatedAmount) < parseFloat(saleAmount)) {
                    const balanceAmount = (saleAmount - updatedAmount).toFixed(2);
                    return {
                        ...invdetail,
                        Amount: parseFloat(updatedAmount),
                        BalanceAmount: parseFloat(balanceAmount),
                    };
                } else {
                    return {
                        ...invdetail,
                        Amount: parseFloat(saleAmount),
                        BalanceAmount: 0,
                    };
                }
            }
            return invdetail;
        });

        let totalBilledAmount = 0;
        updatedInvoicedeatils.forEach((invdetail) => {
            totalBilledAmount += parseFloat(invdetail.Amount);
        });

        setFormData((prevState) => ({
            ...prevState,
            Amount: totalBilledAmount.toFixed(2),
        }));

        setInvoicedeatils(updatedInvoicedeatils);
    };

    return (
        <Fragment>
            <div className="form-container">
                <div className="form-card">
                    {/* Header */}
                    <div className="form-header">
                        <h1 className="form-title">Create Rent Receipt</h1>
                        <div className="form-actions">
                            <Link to="/ReceiptIndex" className="btn-back">
                                <ArrowBackIcon fontSize="small" />
                                Back to List
                            </Link>
                            <button className="btn-save" onClick={submitData} disabled={isLoading}>
                                {isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <>
                                        <SaveIcon fontSize="small" />
                                        Save
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && <div className="message error">{errorMessage}</div>}
                    {successMessage && <div className="message success">{successMessage}</div>}

                    {/* Form Body */}
                    <div className="form-body">
                        {/* Row 1: Customer, Bill Date, Bill Number */}
                        <div className="form-row">
                            <div className="form-group form-select" style={{ flex: 2 }}>
                                <label className="form-label">Customer *</label>
                                <Select
                                    options={customerOptions}
                                    value={selectedCustomer}
                                    onChange={handleCustomerSelect}
                                    placeholder="Select a customer"
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Date *</label>
                                <input
                                    className="form-input"
                                    value={formatDateForInput(formData.BillDate)}
                                    type="date"
                                    name="BillDate"
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bill Number</label>
                                <input
                                    className="form-input"
                                    value={formData.BillNo}
                                    type="text"
                                    name="BillNo"
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc' }}
                                />
                            </div>
                        </div>

                        {/* Row 2: Remarks, Amount, Payment Type */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Remarks</label>
                                <input
                                    className="form-input"
                                    value={formData.Remarks}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter remarks"
                                    name="Remarks"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Amount *</label>
                                <input
                                    className="form-input"
                                    value={formData.Amount !== undefined ? parseFloat(formData.Amount).toFixed(2) : 0}
                                    type="text"
                                    placeholder="Enter amount"
                                    onChange={handleChange}
                                    name="Amount"
                                    style={{ textAlign: 'right' }}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Payment Type</label>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '42px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="1"
                                            checked={selectedPaymentType === "1"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cash
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="2"
                                            checked={selectedPaymentType === "2"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        Cheque
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="PaymentType"
                                            value="3"
                                            checked={selectedPaymentType === "3"}
                                            onChange={handleChange}
                                            style={{ accentColor: '#1e3a5f' }}
                                        />
                                        UPI
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Details Table */}
                        {invoicedeatils.length > 0 && (
                            <div className="form-section">
                                <h3 className="section-title">Rent Bill Details</h3>
                                <div className="table-container">
                                    <div className="table-wrapper">
                                        <table className="customer-table">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th style={{ textAlign: 'right' }}>Rent Amount</th>
                                                    <th style={{ textAlign: 'right' }}>Pending Amount</th>
                                                    <th style={{ textAlign: 'right' }}>Paid Amount</th>
                                                    <th style={{ textAlign: 'right' }}>Balance Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoicedeatils.map((invdetail, index) => (
                                                    <tr key={index}>
                                                        <td className="cell-name">{invdetail.RentDescription}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            {parseFloat(invdetail.RentAmount).toFixed(2)}
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            {parseFloat(invdetail.SaleAmount).toFixed(2)}
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <input
                                                                type="number"
                                                                value={invdetail.Amount !== undefined ? parseFloat(invdetail.Amount).toFixed() : 0}
                                                                onChange={(e) => handleCalculation(e, index, 'Amount')}
                                                                name="Amount"
                                                                className="form-input"
                                                                style={{ 
                                                                    width: '120px', 
                                                                    textAlign: 'right',
                                                                    padding: '6px 10px'
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>
                                                            {invdetail.BalanceAmount !== undefined ? parseFloat(invdetail.BalanceAmount).toFixed(2) : '0.00'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: '600', color: '#1e3a5f' }}>
                                                        Total:
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                                                        {parseFloat(formData.Amount || 0).toFixed(2)}
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>
                                                        {invoicedeatils.reduce((sum, inv) => sum + (parseFloat(inv.BalanceAmount) || 0), 0).toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default ReceiptCreate;
